<script setup lang="ts">
import type { NavigationMenuItem } from '@nuxt/ui'
import {
  ROLE_IDS,
  getMenuAccessCheckerByContext,
  resolveRoleId,
  type MenuKey,
} from '~/utils/roleAccess'

const userStore = useUserStore()
const { fullName, avatarUrl, role, policyIds } = storeToRefs(userStore)
const runtimeConfig = useRuntimeConfig()
const route = useRoute()

const isNonProduction = computed(() => {
  const appUrl = String(runtimeConfig.public?.appUrl || '').toLowerCase()
  if (appUrl.includes('ambulancepresent.be') && !appUrl.includes('dev') && !appUrl.includes('staging') && !appUrl.includes('localhost'))
    return false

  return import.meta.dev || appUrl.includes('localhost') || appUrl.includes('dev') || appUrl.includes('staging')
})

const menuItemsByRoleIdCache = new Map<string, ReadonlyArray<ReadonlyArray<NavigationMenuItem>>>()

type SidebarMenuItem = Omit<NavigationMenuItem, 'children'> & {
  key: MenuKey
  children?: SidebarMenuItem[]
}

const freezeMenuItems = (menuItems: NavigationMenuItem[][]): ReadonlyArray<ReadonlyArray<NavigationMenuItem>> => {
  const frozenGroups = menuItems.map(group => Object.freeze(group))
  return Object.freeze(frozenGroups)
}

const filterMenuItemsByPermission = (
  items: SidebarMenuItem[],
  canAccessMenu: (menuKey: MenuKey) => boolean,
): NavigationMenuItem[] => {
  return items.flatMap((item) => {
    const allowedSelf = canAccessMenu(item.key)
    const visibleChildren = item.children
      ? filterMenuItemsByPermission(item.children, canAccessMenu)
      : undefined
    const hasVisibleChildren = Boolean(visibleChildren?.length)

    if (!allowedSelf && !hasVisibleChildren)
      return []

    const { key, children, ...menuItem } = item
    if (!children)
      return [menuItem]

    if (hasVisibleChildren)
      return [{ ...menuItem, children: visibleChildren }]

    if ('to' in menuItem)
      return [menuItem]

    return []
  })
}

const allItems: SidebarMenuItem[][] = [
  [
    {
      key: 'home',
      label: 'Accueil',
      icon: 'i-lucide-house',
      to: '/volontaires',
    },
    {
      key: 'shop',
      label: 'Boutique',
      icon: 'i-lucide-shopping-cart',
      to: '/volontaires/boutique',
    },
    {
      key: 'orders',
      label: 'Mes commandes',
      icon: 'i-lucide-package',
      to: '/volontaires/commandes',
    },
    {
      key: 'returns',
      label: 'Mes retours',
      icon: 'i-lucide-package-open',
      to: '/volontaires/retours',
    },
    {
      key: 'garments',
      label: 'Mes tenues',
      icon: 'i-lucide-shirt',
      to: '/volontaires/tenues',
    },
    {
      key: 'dispatching',
      label: 'Dispatching',
      icon: 'i-lucide-headset',
      to: '/volontaires/dispatching',
    },
    {
      key: 'dispatchingOnCall',
      label: 'Permanence',
      icon: 'i-lucide-shield-user',
      to: '/volontaires/dispatching/permanence',
    },
    {
      key: 'directory',
      label: 'Annuaire',
      icon: 'i-lucide-bookmark',
      to: '/volontaires/annuaire',
    },
    {
      key: 'documents',
      label: 'Documents',
      icon: 'i-lucide-file-text',
      to: 'https://cloud.ambulancepresent.be/s/8bXQaCNydLsPnNf',
    },
  ],
  [
    {
      key: 'settings',
      label: 'Paramètres',
      icon: 'i-lucide-settings',
      to: '/volontaires/parametres',
    },
    {
      key: 'support',
      label: 'Support',
      icon: 'i-lucide-life-buoy',
      to: '/volontaires/support',
    },
    {
      key: 'ebrigade',
      label: 'eBrigade',
      icon: 'i-lucide-external-link',
      to: 'https://ambulancepresent.ebrigade.be',
      target: '_blank',
    },
    {
      key: 'mail',
      label: 'E-mail',
      icon: 'i-lucide-mail-open',
      to: 'https://outlook.cloud.microsoft/mail/',
      target: '_blank',
    },
    {
      key: 'backToSite',
      label: 'Retour au site',
      icon: 'i-lucide-arrow-left',
      to: '/',
    },
  ],
  [
    {
      key: 'adminUsersList',
      label: 'Utilisateurs',
      icon: 'i-lucide-users',
      to: '/volontaires/admin/utilisateurs',
    },
    {
      key: 'adminRecruitment',
      label: 'Recrutement',
      icon: 'i-lucide-user-plus',
      to: '/volontaires/admin/recrutement',
    },
    {
      key: 'adminFormationsList',
      label: 'Formations',
      icon: 'i-lucide-graduation-cap',
      to: '/volontaires/admin/formations',
    },
    {
      key: 'adminCaPoints',
      label: 'Points CA',
      icon: 'i-lucide-scale',
      to: '/volontaires/admin/points-ca',
    },
  ],
  [
    {
      key: 'adminDispatchPlanning',
      label: 'Planning',
      icon: 'i-lucide-calendar-days',
      to: '/volontaires/admin/dispatching',
    },
    {
      key: 'adminDispatchMonth',
      label: 'Préparation du mois',
      icon: 'i-lucide-calendar-clock',
      children: [
        {
          key: 'adminDispatchSettings',
          label: 'Mois et paramètres',
          icon: 'i-lucide-calendar-clock',
          to: '/volontaires/admin/dispatching/parametres',
        },
        {
          key: 'adminDispatchCoverage',
          label: 'Couverture',
          icon: 'i-lucide-calendar-range',
          to: '/volontaires/admin/dispatching/couverture',
        },
        {
          key: 'adminDispatchEquity',
          label: 'Équité',
          icon: 'i-lucide-scale',
          to: '/volontaires/admin/dispatching/equite',
        },
      ],
    },
    {
      key: 'adminDispatchDuty',
      label: 'Permanences et téléphonie',
      icon: 'i-lucide-shield-user',
      children: [
        {
          key: 'adminDispatchOnCall',
          label: 'Permanences',
          icon: 'i-lucide-shield-user',
          to: '/volontaires/admin/dispatching/permanence',
        },
        {
          key: 'adminDispatchPhone',
          label: 'Téléphonie',
          icon: 'i-lucide-phone-forwarded',
          to: '/volontaires/admin/dispatching/telephonie',
        },
      ],
    },
    {
      key: 'adminDispatchDepartures',
      label: 'Départs',
      icon: 'i-lucide-user-minus',
      to: '/volontaires/admin/dispatching/departs',
    },
  ],
  [
    {
      key: 'adminDmp',
      label: 'Notification DMP',
      icon: 'i-lucide-file-heart',
      to: '/volontaires/admin/dmp',
    },
    {
      key: 'adminFibam',
      label: 'Bilan FIBAM',
      icon: 'i-lucide-clipboard-check',
      to: '/volontaires/admin/fibam',
    },
    {
      key: 'adminDmpDocuments',
      label: 'Documents DMP & FIBAM',
      icon: 'i-lucide-folder-open',
      to: '/volontaires/admin/dmp/documents',
    },
  ],
  [
    {
      key: 'adminOrdersList',
      label: 'Commandes',
      icon: 'i-lucide-package',
      to: '/volontaires/admin/commandes',
    },
    {
      key: 'adminReturns',
      label: 'Retours',
      icon: 'i-lucide-package-open',
      to: '/volontaires/admin/retours',
    },
    {
      key: 'adminShop',
      label: 'Catalogue',
      icon: 'i-lucide-store',
      to: '/volontaires/admin/boutique',
    },
  ],
  [
    {
      key: 'adminLogistiqueDashboard',
      label: 'Tableau de bord',
      icon: 'i-lucide-layout-dashboard',
      to: '/volontaires/admin/logistique',
    },
    {
      key: 'adminTenues',
      label: 'Tenues',
      icon: 'i-lucide-shirt',
      children: [
        {
          key: 'adminTenuesStock',
          label: 'Stock et affectations',
          icon: 'i-lucide-boxes',
          to: '/volontaires/admin/tenues',
        },
        {
          key: 'adminTenuesReturns',
          label: 'Retours de tenues',
          icon: 'i-lucide-package-open',
          to: '/volontaires/admin/tenues/retours',
        },
        {
          key: 'adminTenuesTypes',
          label: 'Types de tenue',
          icon: 'i-lucide-tags',
          to: '/volontaires/admin/tenues/types',
        },
        {
          key: 'adminTenuesVolunteers',
          label: 'Volontaires',
          icon: 'i-lucide-user-round-search',
          to: '/volontaires/admin/tenues/volontaires',
        },
        {
          key: 'adminTenuesInventory',
          label: 'Inventaire',
          icon: 'i-lucide-clipboard-check',
          to: '/volontaires/admin/tenues/inventaire',
        },
        {
          key: 'adminTenuesDepartures',
          label: 'Départs',
          icon: 'i-lucide-user-round-minus',
          to: '/volontaires/admin/tenues/departs',
        },
        {
          key: 'adminTenuesStats',
          label: 'Statistiques',
          icon: 'i-lucide-chart-column',
          to: '/volontaires/admin/tenues/statistiques',
        },
      ],
    },
    {
      key: 'adminLogistiqueTelecomList',
      label: 'Télécommunications',
      icon: 'i-lucide-smartphone',
      to: '/volontaires/admin/logistique/telecom',
    },
    {
      key: 'adminLogistiqueBadgeGenerator',
      label: 'Badges',
      icon: 'i-lucide-credit-card',
      to: '/volontaires/admin/logistique/badge/generator',
    },
  ],
]

const items = computed<NavigationMenuItem[][]>(() => {
  const roleId = resolveRoleId(role.value)
  const sortedPolicyIds = [...(policyIds.value ?? [])].sort()
  const cacheKey = `${roleId}::${sortedPolicyIds.join(',')}`
  const cachedItems = menuItemsByRoleIdCache.get(cacheKey)
  if (cachedItems)
    return cachedItems as NavigationMenuItem[][]

  const canAccessMenu = getMenuAccessCheckerByContext(roleId, sortedPolicyIds)
  const filteredItems = allItems.map(group => filterMenuItemsByPermission(group, canAccessMenu))
  const frozenItems = freezeMenuItems(filteredItems)

  menuItemsByRoleIdCache.set(cacheKey, frozenItems)
  return frozenItems as NavigationMenuItem[][]
})

const hasPolicyBasedAdminAccess = computed(() => {
  if (!isNonProduction.value)
    return false

  const roleId = resolveRoleId(role.value)
  if (roleId === ROLE_IDS.ADMIN)
    return false

  const sortedPolicyIds = [...(policyIds.value ?? [])].sort()
  if (!sortedPolicyIds.length)
    return false

  const canAccessMenu = getMenuAccessCheckerByContext(roleId, sortedPolicyIds)
  return canAccessMenu('adminCa') || canAccessMenu('adminFormations')
})

const adminSectionLabels = ['Volontaires', 'Dispatching', 'DMP', 'Boutique', 'Logistique']

const adminSections = computed(() => adminSectionLabels
  .map((label, index) => ({
    label,
    items: (items.value[index + 2] ?? []).map(item => item.children?.length
      && item.children.some(child => typeof child.to === 'string' && route.path.startsWith(child.to))
      ? { ...item, defaultOpen: true }
      : item),
  }))
  .filter(section => section.items.length > 0))
</script>

<template>
  <UDashboardSidebar
    class="print:hidden"
    collapsible
    resizable
    :ui="{ footer: 'border-t border-default' }"
  >
    <template #header="{ collapsed }">
      <UColorModeImage
        light="/images/logo.png"
        dark="/images/logo-light.png"
        class="mx-auto"
        :class="collapsed ? 'h-8' : 'h-14'"
        alt="Logo AP 2.0"
        loading="lazy"
      />
    </template>

    <template #default="{ collapsed }">
      <UNavigationMenu
        :collapsed="collapsed"
        :items="items[0] ?? []"
        orientation="vertical"
      />

      <div
        v-if="adminSections.length > 0 && !collapsed"
        class="mt-4 flex items-center gap-2 px-2"
      >
        <p class="text-xs font-semibold uppercase tracking-wide text-muted">
          Administration
        </p>
        <UBadge
          v-if="hasPolicyBasedAdminAccess"
          color="warning"
          variant="soft"
          size="xs"
          label="Accès via policy"
        />
      </div>
      <USeparator
        v-else-if="adminSections.length > 0"
        class="my-2"
      />

      <div
        v-for="section in adminSections"
        :key="section.label"
        class="mt-2"
      >
        <p
          v-if="!collapsed"
          class="px-2.5 pb-1 text-[11px] text-dimmed"
        >
          {{ section.label }}
        </p>
        <UNavigationMenu
          :collapsed="collapsed"
          :items="section.items"
          orientation="vertical"
        />
      </div>

      <UNavigationMenu
        :collapsed="collapsed"
        :items="items[1] ?? []"
        orientation="vertical"
        class="mt-auto"
      />
    </template>

    <template #footer="{ collapsed }">
      <div class="space-y-2">
        <UButton
          :avatar="{
            src: avatarUrl,
          }"
          :label="collapsed ? undefined : fullName || 'Utilisateur'"
          to="/volontaires/deconnexion"
          trailing-icon="i-lucide-log-out"
          color="neutral"
          variant="ghost"
          class="w-full"
          :block="collapsed"
        />
      </div>
    </template>
  </UDashboardSidebar>
</template>
